import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { CALENDAR_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the Calendar gate (`/api/flags/calendar`, Conductor release `symphonyCalendar`).
 *
 * Fails CLOSED: no Calendar screen, card or tile. A dev-tools Feature toggles override wins over the server.
 * Memoized per page load, so the Calendar never disappears under someone using it; only a real answer is
 * remembered.
 */
const GATE_TIMEOUT_MS = 2500;

let cached: Promise<boolean> | null = null;
let known: boolean | null = null;

export function calendarEnabled(): Promise<boolean> {
  const forced = toggleOverride(CALENDAR_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/calendar`, { credentials: 'include', signal: AbortSignal.timeout(GATE_TIMEOUT_MS) });
      if (res.ok) return (known = ((await res.json()) as boolean) === true);
      // Local dev sends API calls to production, which has no such route until this ships.
      if (process.env.NODE_ENV === 'development' && res.status === 404) return (known = true);
    } catch {
      // closed for now; asked again by the next reader
    }
    cached = null;
    return false;
  })();
  return cached;
}

const RETRY_MS = 3000;
const ATTEMPTS = 4;

/**
 * `resolved` is false until a real answer, so a screen can wait rather than send the reader away; a read
 * that timed out or failed is asked again, and only a server that keeps failing counts as "no".
 */
export function useCalendarGate(): { enabled: boolean; resolved: boolean } {
  // Storage is not read here: the server renders without it, so the first client render must match.
  const [state, setState] = useState(() => ({ enabled: known ?? false, resolved: known !== null }));
  useEffect(() => {
    let live = true;
    let timer: number | undefined;
    let attempts = 0;
    const ask = () => {
      attempts += 1;
      void calendarEnabled().then((enabled) => {
        if (!live) return;
        const answered = known !== null || toggleOverride(CALENDAR_SPEC) !== undefined;
        setState({ enabled, resolved: answered || attempts >= ATTEMPTS });
        if (!answered && attempts < ATTEMPTS) timer = window.setTimeout(ask, RETRY_MS);
      });
    };
    ask();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, []);
  return state;
}
